import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { passwordSchema } from '@tt/shared';
import { Alert } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

interface PasswordFormProps {
  submitLabel: string;
  onSubmit: (password: string) => Promise<void>;
  /** Extra fields rendered above the password fields (e.g. current password). */
  before?: React.ReactNode;
  autoCompleteNew?: 'new-password';
}

/** New password + confirmation with client-side policy feedback; the server re-validates. */
export function PasswordForm({ submitLabel, onSubmit, before }: PasswordFormProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldError(undefined);
    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message);
      return;
    }
    if (password !== confirm) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }
    setPending(true);
    try {
      await onSubmit(password);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      {error ? <Alert variant="error">{error}</Alert> : null}
      {before}
      <Field
        label={t('auth.newPassword')}
        hint={t('auth.passwordHelp')}
        error={fieldError}
        required
      >
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        )}
      </Field>
      <Field label={t('auth.confirmPassword')} required>
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        )}
      </Field>
      <Button type="submit" className="w-full" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  );
}
